import EmptyState from '@/components/EmptyState';
import HorizontalDatePicker from '@/components/HorizontalDatePicker';
import { useT } from '@/i18n';
import type { Translations } from '@/i18n/locales/en';
import { formatTime, todayString } from '@/utils/datetime';
import { formatMoney } from '@/utils/money';
import type { TableSessionSummary } from '@restaurant/api-client';
import {
  Block,
  BlockTitle,
  List,
  ListItem,
  Navbar,
  Page,
  Preloader,
  Subnavbar,
  f7,
} from 'framework7-react';
import { useState } from 'react';
import { useSessionHistory } from '../hooks/useTableOrdering';

type HistoryStrings = Translations['tableOrdering']['history'];
type PaymentStrings = Translations['tableOrdering']['payment'];

// Small pill for the billing-type tags. Neutral by default; `danger` red for
// the force-closed flag. Kept out of F7's truncating text slots so the values
// never clip.
function Chip({ label, tone = 'neutral' }: { label: string; tone?: 'neutral' | 'danger' }) {
  const c =
    tone === 'danger'
      ? { bg: 'rgba(255,59,48,0.12)', fg: '#ff3b30' }
      : { bg: 'rgba(120,120,128,0.16)', fg: 'var(--f7-text-color)' };
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1.5,
        padding: '1px 8px',
        borderRadius: 6,
        background: c.bg,
        color: c.fg,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

// Split-type tag. Null splitMode + something collected = a single (un-split)
// bill; null + nothing collected (a force-closed comp) shows no split tag.
function splitLabel(s: TableSessionSummary, t: HistoryStrings): string | null {
  switch (s.splitMode) {
    case 'EVEN':
      return t.splitEven;
    case 'BY_AMOUNT':
      return t.splitByAmount;
    case 'BY_SEAT':
      return t.splitBySeat;
    case null:
      return s.paidCents > 0 ? t.single : null;
  }
}

// Payment-method tag, mirroring BillCollectPage's Cash / Card wording.
function payLabel(
  m: TableSessionSummary['paymentMethod'],
  t: HistoryStrings,
  tPayment: PaymentStrings,
): string | null {
  switch (m) {
    case 'CASH':
      return tPayment.cash;
    case 'MIXED':
      return t.mixed;
    case 'MANUAL':
    case 'ONLINE':
    case 'CARD_ON_HANDOFF':
      return t.card;
    case 'GIFT_CARD':
      return t.giftCard;
    case null:
      return null;
  }
}

/**
 * Closed-tab history, organised one local day at a time (default today) via the
 * same horizontal day strip the reservations workspace uses. Staff scan the
 * day's settled tabs and tap through to the full session detail to recheck a
 * bill, payment, or who closed it. Read-only - every row is a finished visit.
 */
export default function TableSessionHistoryPage() {
  const tAll = useT();
  const t = tAll.tableOrdering.history;
  const tWorkspace = tAll.tableOrdering.workspace;
  const [date, setDate] = useState<string>(todayString());
  const { data, isLoading, error } = useSessionHistory(date);
  const sessions = data ?? [];

  const collectedCents = sessions.reduce((sum, s) => sum + s.paidCents, 0);
  const currency = sessions[0]?.currency ?? 'EUR';

  function open(s: TableSessionSummary) {
    f7.views.current.router.navigate(`/tables/session/${s.id}`);
  }

  return (
    <Page name="table-session-history">
      <Navbar title={t.title} backLink={tWorkspace.tables}>
        <Subnavbar>
          <HorizontalDatePicker selectedDate={date} onDateSelect={setDate} />
        </Subnavbar>
      </Navbar>

      {isLoading && !data ? (
        <Block style={{ textAlign: 'center', paddingTop: 48 }}>
          <Preloader size={32} />
        </Block>
      ) : error ? (
        <Block style={{ paddingTop: 32 }}>
          <EmptyState
            icon="exclamationmark_triangle"
            title={t.couldntLoadHistory}
            description={t.tryAgain}
          />
        </Block>
      ) : sessions.length === 0 ? (
        <Block style={{ paddingTop: 32 }}>
          <EmptyState icon="clock" title={t.noClosedTabs} description={t.noClosedTabsDesc} />
        </Block>
      ) : (
        <>
          <BlockTitle medium style={{ marginTop: 16 }}>
            {t.tabsSummary(sessions.length, formatMoney(collectedCents, currency))}
          </BlockTitle>
          <List mediaList dividers strongIos insetIos style={{ marginBottom: 24 }}>
            {sessions.map((s) => {
              const meta = [
                ...(s.zoneLabel ? [s.zoneLabel] : []),
                t.closedAt(formatTime(s.closedAt)),
                t.roundsCount(s.roundCount),
                ...(s.guestName ? [s.guestName] : []),
              ].join(' · ');
              const split = splitLabel(s, t);
              const pay = payLabel(s.paymentMethod, t, tAll.tableOrdering.payment);
              return (
                <ListItem
                  key={s.id}
                  link="#"
                  title={s.tableLabel}
                  subtitle={meta}
                  onClick={() => open(s)}
                >
                  <span slot="after" style={{ fontWeight: 600, color: 'var(--f7-text-color)' }}>
                    {formatMoney(s.totalCents, s.currency)}
                  </span>
                  <div
                    slot="footer"
                    style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}
                  >
                    {split && <Chip label={split} />}
                    {pay && <Chip label={pay} />}
                    {s.closeReason !== null && <Chip label={t.forceClosed} tone="danger" />}
                  </div>
                </ListItem>
              );
            })}
          </List>
        </>
      )}
    </Page>
  );
}
